// MediaDeck.tsx — 2–4 images as a swipeable DECK.
//
// The active photo sits centred and in front at full size; its neighbours peek out behind it,
// inset and dimmed, so it is obvious at a glance that there is more. Drag/swipe left or right
// (or use the arrow keys, or the dots) to bring another one forward.
//
// Tapping the FRONT photo opens the viewer; tapping a neighbour just brings it forward.
//
// ── Why the transform is written INLINE ──────────────────────────────────────────
// Position/opacity/scale are set as inline styles while `transition` lives statically in the
// styled rule. Encoding the slot in styled-component props instead generates a NEW CLASS per
// position, and swapping classes mid-interaction does not reliably animate — the slide
// snapped straight to its new place. Changing only inline values keeps one stable class, so
// the browser always has a from/to pair to interpolate.

import { useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import styled from 'styled-components';
import type { PostImage } from '@/core/models/post';
import { Icon } from '@/icons';
import { mediaCap, mediaCapHero } from './mediaCap';
import { clampSlide, deckAspect, deckItems, slotOf } from './mediaLayout';

/** Drag distance (px) past which the deck advances on release. */
const SWIPE_THRESHOLD = 48;
/** How far a neighbour peeks out from behind the front photo, as a % of the frame. */
const PEEK = 7;
/** How much a neighbour shrinks behind the front photo. */
const SHRINK = 0.9;

const Wrap = styled.div<{ $hero: boolean }>`
  ${({ $hero }) => ($hero ? mediaCapHero : mediaCap)}
  /* Centred in the card rather than pinned left, so the front photo reads as the subject
     and the peeking neighbours sit symmetrically around it. */
  align-self: center;
  width: min(100%, calc(var(--media-cap) * var(--deck-ar)));
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Stage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: var(--deck-ar);
  max-height: var(--media-cap);
  touch-action: pan-y; /* horizontal is ours; vertical must still scroll the feed */
  -webkit-tap-highlight-color: transparent;
`;

/**
 * One photo in the deck. Absolutely positioned so every slide occupies the same box — the
 * stage height therefore never depends on which photo is in front.
 */
const Slide = styled.button`
  appearance: none;
  padding: 0;
  border: 0;
  margin: 0;
  font: inherit;
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;

  /* Static transition: only the inline transform/opacity/filter change, so there is always a
     clean from/to pair to interpolate between. */
  transition:
    transform 340ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 260ms ease,
    filter 260ms ease;

  /* While a finger/mouse is down the slide must track the pointer exactly, not lag behind it. */
  &[data-dragging='true'] {
    transition: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  & > img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 50% 38%; /* faces sit above centre */
    pointer-events: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
`;

/** "1 / 4" badge, bottom-right of the front photo. */
const Counter = styled.span`
  position: absolute;
  right: 10px;
  bottom: 10px;
  z-index: 20; /* above every slide */
  pointer-events: none;
  padding: 3px 9px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: rgba(0, 0, 0, 0.68);
  color: #ffffff;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1.4;
`;

/**
 * ‹ › step buttons, vertically centred over the stage.
 *
 * Always visible rather than hover-only: the whole point is reaching people who cannot
 * comfortably drag — touch users and trackpad users — and a control that only appears on
 * hover is exactly the one they cannot find. Being real <button>s, they also give keyboard
 * users a focusable target instead of only the arrow-key handler on the stage.
 */
const NavButton = styled.button<{ $side: 'prev' | 'next' }>`
  appearance: none;
  border: 0;
  margin: 0;
  padding: 0;
  font: inherit;
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'prev' ? 'left: 8px;' : 'right: 8px;')}
  /* translateY only: horizontal placement is left/right so this never fights the slides'
     inline transforms. */
  transform: translateY(-50%);
  z-index: 25; /* above every slide (max 10) and the counter (20) */
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: rgba(0, 0, 0, 0.62);
  color: #ffffff;
  cursor: pointer;
  transition:
    background 160ms ease,
    opacity 160ms ease;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.82);
  }

  /* Dimmed rather than removed at the ends: the deck clamps instead of wrapping, so a
     disabled arrow is the cue that you have reached the edge. */
  &:disabled {
    opacity: 0.32;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Dots = styled.div`
  display: flex;
  gap: 6px;
  justify-content: center;
`;

const Dot = styled.button<{ $on: boolean }>`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 6px 3px; /* comfortable hit area around a small bar */
  cursor: pointer;
  &::after {
    content: '';
    display: block;
    width: ${({ $on }) => ($on ? '18px' : '6px')};
    height: 4px;
    border-radius: 2px;
    background: ${({ theme, $on }) => ($on ? theme.color.accentCyan : theme.color.hairSoft)};
    transition:
      width 200ms ease,
      background 200ms ease;
  }
  @media (prefers-reduced-motion: reduce) {
    &::after {
      transition: none;
    }
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

export interface MediaDeckProps {
  items: PostImage[];
  variant?: 'feed' | 'hero';
  priority?: boolean;
  onOpen?: (index: number) => void;
}

export function MediaDeck({ items, variant = 'feed', priority = false, onOpen }: MediaDeckProps) {
  const slides = deckItems(items);
  const ar = deckAspect(slides);
  const total = slides.length;

  const [active, setActive] = useState(0);
  const [drag, setDrag] = useState(0);
  const dragStart = useRef<number | null>(null);

  const go = (next: number) => setActive(clampSlide(next, total));

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    dragStart.current = e.clientX;
    // Capture so the gesture keeps tracking even if the pointer leaves the frame.
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (dragStart.current === null) return;
    setDrag(e.clientX - dragStart.current);
  }

  function onPointerUp() {
    if (dragStart.current === null) return;
    const d = drag;
    dragStart.current = null;
    setDrag(0);
    if (Math.abs(d) < SWIPE_THRESHOLD) return;
    go(active + (d < 0 ? 1 : -1)); // drag left -> next
  }

  const dragging = drag !== 0;

  return (
    <Wrap
      $hero={variant === 'hero'}
      style={{ ['--deck-ar' as string]: String(ar) } as CSSProperties}
      data-testid="post-media-deck"
      data-count={total}
    >
      <Stage
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            go(active + 1);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            go(active - 1);
          }
        }}
      >
        {slides.map((item, i) => {
          const slot = slotOf(i, active);
          const isFront = slot === 0;
          const parked = Math.abs(slot) > 1;
          // Neighbours follow the drag at a reduced rate so the stack feels connected
          // without sliding as far as the photo under the finger.
          const dragPx = isFront ? drag : drag * 0.4;
          return (
            <Slide
              key={`${item.url}-${i}`}
              type="button"
              data-dragging={dragging ? 'true' : 'false'}
              style={{
                transform: `translate3d(calc(${slot * PEEK}% + ${dragPx}px), 0, 0) scale(${isFront ? 1 : SHRINK})`,
                opacity: parked ? 0 : 1,
                filter: isFront ? 'none' : 'brightness(0.55)',
                zIndex: 10 - Math.abs(slot),
                pointerEvents: parked ? 'none' : 'auto',
                cursor: isFront ? 'zoom-in' : 'pointer',
              }}
              aria-hidden={parked}
              tabIndex={isFront ? 0 : -1}
              aria-label={
                isFront
                  ? item.alt
                    ? `Open image ${i + 1} of ${total}: ${item.alt}`
                    : `Open image ${i + 1} of ${total}`
                  : `Show image ${i + 1} of ${total}`
              }
              onClick={() => {
                // A swipe ends with a click event; ignore it so swiping never opens the viewer.
                if (Math.abs(drag) > 4) return;
                if (isFront) onOpen?.(i);
                else go(i);
              }}
            >
              <img
                src={item.url}
                alt={item.alt || ''}
                loading={priority && i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                draggable={false}
              />
            </Slide>
          );
        })}

        <Counter data-testid="deck-counter" aria-hidden="true">
          {active + 1} / {total}
        </Counter>

        {total > 1 &&
          (
            [
              { side: 'prev', icon: 'chevron-left', label: 'Previous image', step: -1 },
              { side: 'next', icon: 'chevron-right', label: 'Next image', step: 1 },
            ] as const
          ).map(({ side, icon, label, step }) => (
            <NavButton
              key={side}
              $side={side}
              type="button"
              data-testid={`deck-${side}`}
              aria-label={label}
              disabled={step < 0 ? active === 0 : active === total - 1}
              // Keep the press off the stage's drag handlers — otherwise pressing an arrow
              // would also begin a drag gesture.
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => {
                // A drag that happens to end over an arrow still ends in a click; ignore it
                // so a swipe never advances twice. Mirrors the slides' own guard.
                if (Math.abs(drag) > 4) return;
                go(active + step);
              }}
            >
              <Icon name={icon} size={18} />
            </NavButton>
          ))}
      </Stage>

      <Dots role="tablist" aria-label={`Image ${active + 1} of ${total}`}>
        {slides.map((item, i) => (
          <Dot
            key={`dot-${item.url}-${i}`}
            type="button"
            role="tab"
            $on={i === active}
            aria-selected={i === active}
            aria-label={`Show image ${i + 1} of ${total}`}
            onClick={() => go(i)}
          />
        ))}
      </Dots>
    </Wrap>
  );
}
